import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Statistic,
  Table,
  Spin,
  Typography,
  Tag,
  Button,
  Space,
  Input,
} from "antd";
import {
  UserOutlined,
  UserAddOutlined,
  CrownOutlined,
  ReloadOutlined,
  SearchOutlined,
  FileExcelOutlined,
} from "@ant-design/icons";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
} from "recharts";
import axios from "axios";
import BASEURL from "../API/Config";
import * as XLSX from "xlsx";

const { Text } = Typography;
const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884d8", "#82ca9d"];

const CustomerReports = () => {
  const [loading, setLoading] = useState(true);
  const [registrations, setRegistrations] = useState([]);
  const [activity, setActivity] = useState([]);
  const [topCustomers, setTopCustomers] = useState([]);
  const [geographic, setGeographic] = useState([]);

  const [customerSearch, setCustomerSearch] = useState("");

  const token = localStorage.getItem("token");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const config = {
        headers: { Authorization: `Bearer ${token}` },
      };

      const [regRes, actRes, topRes, geoRes] = await Promise.all([
        axios.get(`${BASEURL}/reports/customer-registrations`, config),
        axios.get(`${BASEURL}/reports/customer-activity`, config),
        axios.get(`${BASEURL}/reports/top-customers`, config),
        axios.get(`${BASEURL}/reports/geographic-distribution`, config),
      ]);

      setRegistrations(regRes.data);
      setActivity(actRes.data);
      setTopCustomers(topRes.data);
      setGeographic(geoRes.data);
    } catch (error) {
      console.error("Error fetching customer reports:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = (data, fileName, columns) => {
    let exportData = data;
    if (columns) {
      exportData = data.map((item, index) => {
        const row = { "S.No": index + 1 };
        columns.forEach((col) => {
          row[col.title] = col.dataIndex ? item[col.dataIndex] : col.render ? col.render(null, item) : "";
        });
        return row;
      });
    }
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Report");
    XLSX.writeFile(wb, `${fileName}.xlsx`);
  };

  const downloadAllReports = () => {
    const wb = XLSX.utils.book_new();
    const sections = [
      { name: "Registrations", data: registrations, cols: [{ title: "Month", dataIndex: "_id" }, { title: "Count", dataIndex: "count" }] },
      { name: "Activity", data: activity, cols: [{ title: "Status", dataIndex: "_id" }, { title: "Count", dataIndex: "count" }] },
      { name: "Top Customers", data: topCustomers, cols: [{ title: "Name", dataIndex: "name" }, { title: "Email", dataIndex: "email" }, { title: "CLV (Total Spent)", dataIndex: "totalSpent" }, { title: "Orders", dataIndex: "orderCount" }] },
      { name: "Geographic", data: geographic, cols: [{ title: "City", dataIndex: "_id" }, { title: "Orders", dataIndex: "orderCount" }, { title: "Revenue", dataIndex: "revenue" }] }
    ];

    sections.forEach(section => {
      const exportData = section.data.map((item, index) => {
        const row = { "S.No": index + 1 };
        section.cols.forEach((col) => {
          row[col.title] = col.dataIndex ? item[col.dataIndex] : "";
        });
        return row;
      });
      const ws = XLSX.utils.json_to_sheet(exportData);
      XLSX.utils.book_append_sheet(wb, ws, section.name);
    });

    XLSX.writeFile(wb, "Customer_Analytics_Full_Report.xlsx");
  };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "80vh" }}>
        <Spin size="large" tip="Analyzing Customer Data..." />
      </div>
    );
  }

  const filteredCustomers = topCustomers.filter(c => 
    c.name?.toLowerCase().includes(customerSearch.toLowerCase()) || 
    String(c.phoneNumber || "").includes(customerSearch)
  );

  return (
    <div style={{ padding: "24px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 1 }}>Reports</div>
          <h2 style={{ margin: 0, fontFamily: "Outfit,sans-serif", fontSize: 22, fontWeight: 700, color: "#212121" }}>Customer Analytics & Insights</h2>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={fetchData} style={{ borderRadius: 4 }}>Refresh</Button>
          <Button 
            type="primary" 
            icon={<FileExcelOutlined />} 
            onClick={downloadAllReports}
            style={{ background: "#107c41", borderColor: "#107c41", borderRadius: 4, fontWeight: 600 }}
          >
            Download Full Insights
          </Button>
        </Space>
      </div>

      {/* Top Level Stats */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={8}>
          <div style={{ background: "#fff", padding: 20, borderRadius: 8, border: "1px solid #e8ecf0", boxShadow: "0 1px 3px rgba(0,0,0,0.02)" }}>
            <Statistic
              title={<span style={{ fontSize: 13, fontWeight: 600, color: "#6b7280" }}>Total Customers</span>}
              value={activity.reduce((acc, curr) => acc + curr.count, 0)}
              prefix={<UserOutlined style={{ color: "#2874f0" }} />}
              valueStyle={{ color: "#212121", fontWeight: 700 }}
            />
          </div>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <div style={{ background: "#fff", padding: 20, borderRadius: 8, border: "1px solid #e8ecf0", boxShadow: "0 1px 3px rgba(0,0,0,0.02)" }}>
            <Statistic
              title={<span style={{ fontSize: 13, fontWeight: 600, color: "#6b7280" }}>Active Customers (90d)</span>}
              value={activity.find(a => a._id === "Active")?.count || 0}
              valueStyle={{ color: "#3f8600", fontWeight: 700 }}
              prefix={<UserAddOutlined style={{ color: "#3f8600" }} />}
            />
          </div>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <div style={{ background: "#fff", padding: 20, borderRadius: 8, border: "1px solid #e8ecf0", boxShadow: "0 1px 3px rgba(0,0,0,0.02)" }}>
            <Statistic
              title={<span style={{ fontSize: 13, fontWeight: 600, color: "#6b7280" }}>Inactive Rate</span>}
              value={
                (activity.find(a => a._id === "Inactive")?.count / activity.reduce((acc, curr) => acc + curr.count, 0) * 100).toFixed(1)
              }
              suffix="%"
              valueStyle={{ color: "#cf1322", fontWeight: 700 }}
            />
          </div>
        </Col>
      </Row>

      <Row gutter={[24, 24]}>
        {/* Registration Trends */}
        <Col xs={24} lg={16}>
          <div style={{ background: "#fff", borderRadius: 8, border: "1px solid #e8ecf0", overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
            <div style={{ padding: "14px 20px", borderBottom: "1px solid #f3f4f6", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontWeight: 700, color: "#212121", fontSize: 15 }}>Customer Acquisition Trend</span>
              <Button size="small" icon={<FileExcelOutlined />} onClick={() => handleExport(registrations, "Customer_Registrations", [{ title: "Month", dataIndex: "_id" }, { title: "Count", dataIndex: "count" }])} style={{ borderRadius: 4 }}>Export</Button>
            </div>
            <div style={{ padding: 20, height: 350 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={registrations}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                  <XAxis dataKey="_id" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: "#6b7280" }} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: "#6b7280" }} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="count" name="New Signups" stroke="#1890ff" strokeWidth={3} dot={{ r: 4, fill: "#1890ff" }} activeDot={{ r: 6 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </Col>

        {/* Activity Status Pie */}
        <Col xs={24} lg={8}>
          <div style={{ background: "#fff", borderRadius: 8, border: "1px solid #e8ecf0", overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.05)", height: "100%" }}>
            <div style={{ padding: "14px 20px", borderBottom: "1px solid #f3f4f6" }}>
              <span style={{ fontWeight: 700, color: "#212121", fontSize: 15 }}>Active vs Inactive</span>
            </div>
            <div style={{ padding: 20, height: 350 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={activity}
                    dataKey="count"
                    nameKey="_id"
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {activity.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry._id === "Active" ? "#52c41a" : "#ff4d4f"} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend verticalAlign="bottom" height={36} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </Col>
      </Row>

      <Row gutter={[24, 24]} style={{ marginTop: "24px" }}>
        {/* Top Customers Table */}
        <Col xs={24} lg={14}>
          <div style={{ background: "#fff", borderRadius: 8, border: "1px solid #e8ecf0", overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
            <div style={{ padding: "14px 20px", borderBottom: "1px solid #f3f4f6", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "nowrap" }}>
              <span style={{ fontWeight: 700, color: "#212121", fontSize: 15, whiteSpace: "nowrap" }}>Top Customers (by CLV)</span>
              <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1, justifyContent: "flex-end" }}>
                <Input 
                  prefix={<SearchOutlined style={{ color: "#9ca3af" }} />} 
                  placeholder="Search name/phone..."
                  size="small"
                  value={customerSearch}
                  onChange={(e) => setCustomerSearch(e.target.value)}
                  style={{ width: 180, borderRadius: 4 }} 
                />
                <Button size="small" icon={<FileExcelOutlined />} onClick={() => handleExport(topCustomers, "Top_Customers", [
                  { title: "Name", dataIndex: "name" },
                  { title: "Email", dataIndex: "email" },
                  { title: "Orders", dataIndex: "orderCount" },
                  { title: "Total Spent", dataIndex: "totalSpent" }
                ])} style={{ borderRadius: 4 }}>Export</Button>
              </div>
            </div>
            <Table
              dataSource={filteredCustomers}
              columns={[
                { title: "S.No", key: "index", width: 60, render: (_, __, i) => <span style={{ color: "#9ca3af" }}>{i + 1}</span> },
                { title: "Customer Name", dataIndex: "name", key: "name", render: (n) => <span style={{ fontWeight: 600 }}><CrownOutlined style={{ color: "#faad14", marginRight: 4 }} /> {n}</span> },
                { title: "Orders", dataIndex: "orderCount", key: "orders", align: "center" },
                { title: "Total Spent", dataIndex: "totalSpent", key: "spent", render: (s) => <span style={{ fontWeight: 700 }}>₹{s.toLocaleString()}</span>, sorter: (a, b) => a.totalSpent - b.totalSpent },
                { title: "Phone", dataIndex: "phoneNumber", key: "phone" },
              ]}
              pagination={{ pageSize: 5 }}
              size="middle"
              rowKey="email"
            />
          </div>
        </Col>

        {/* Geographic Distribution */}
        <Col xs={24} lg={10}>
          <div style={{ background: "#fff", borderRadius: 8, border: "1px solid #e8ecf0", overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.05)", height: "100%" }}>
            <div style={{ padding: "14px 20px", borderBottom: "1px solid #f3f4f6", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontWeight: 700, color: "#212121", fontSize: 15 }}>Geographic Distribution</span>
              <Button size="small" icon={<FileExcelOutlined />} onClick={() => handleExport(geographic, "Geographic_Distribution", [{ title: "City", dataIndex: "_id" }, { title: "Revenue", dataIndex: "revenue" }])} style={{ borderRadius: 4 }}>Export</Button>
            </div>
            <div style={{ padding: 20 }}>
              <div style={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={geographic.slice(0, 5)} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f0f0f0" />
                    <XAxis type="number" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: "#6b7280" }} />
                    <YAxis dataKey="_id" type="category" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: "#6b7280" }} width={80} />
                    <Tooltip cursor={{ fill: "rgba(114, 46, 209, 0.05)" }} />
                    <Bar dataKey="revenue" fill="#722ed1" name="Revenue (₹)" radius={[0, 4, 4, 0]} barSize={20} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div style={{ marginTop: 10 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>Showing top 5 cities by revenue distribution</Text>
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default CustomerReports;
